import { useEffect, useState } from 'react'
import { Loader2, PauseCircle, PlayCircle, Gavel, FileCheck2 } from 'lucide-react'
import api from '@/lib/api'

/**
 * Etapa técnica do RDC — visível só a Comercial, Tecnologia, Financeiro e Presidência.
 * O gate não é decidido aqui: o backend devolve `pareceres: null` para quem não pode
 * ver, e o pai só monta este bloco quando vier lista. Regra em um lugar só.
 */

const APROVACAO_OPCOES: Record<string, { v: string; t: string; tom: string }[]> = {
  financeiro: [
    { v: 'aprovado', t: 'Preço cobre o esforço', tom: 'emerald' },
    { v: 'recusado', t: 'Preço não cobre', tom: 'rose' },
  ],
  presidencia: [
    { v: 'sustentado', t: 'Sustentar o prazo', tom: 'emerald' },
    { v: 'reforco_equipe', t: 'Reforçar a equipe', tom: 'sky' },
    { v: 'recusado', t: 'Não atender', tom: 'rose' },
  ],
}

const TOM: Record<string, string> = {
  emerald: 'border-emerald-400 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30',
  sky: 'border-sky-400 text-sky-700 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/30',
  rose: 'border-rose-400 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30',
}

const Campo = ({ rot, children }: { rot: string; children: any }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{rot}</span>
    <div className="mt-1">{children}</div>
  </label>
)

const inputCls = 'w-full rounded-md border bg-background px-3 py-2 text-sm'

export default function RdcDiretoria({ r, onErro, onMudou }:
  { r: any; onErro: (m: string | null) => void; onMudou: () => Promise<void> | void }) {
  const [ocupado, setOcupado] = useState(false)
  const [aba, setAba] = useState<'passagem' | 'parecer' | 'decisao' | 'prazo'>(
    r.status === 'passagem_agendada' ? 'passagem' : 'parecer')

  const [pass, setPass] = useState({
    entendimento_acordado: '', plano_acoes: '', tipo_tecnico: '',
    gravacao_url: '', transcricao_url: '', frente_tecnica: '',
    gestor_tecnico_id: r.gestor_tecnico_id || '',
  })
  const [tecnicos, setTecnicos] = useState<any[]>([])

  // Candidatos a gestor tecnico: quem responde por Tecnologia ou Fabrica.
  useEffect(() => {
    api.get('/v1/users', { params: { per_page: 200 } })
      .then(({ data }) => setTecnicos(
        (data.items || data || []).filter(
          (u: any) => ['tecnologia', 'fabrica'].includes(u.diretoria))))
      .catch(() => setTecnicos([]))
  }, [])
  const [par, setPar] = useState({
    etapa: 'preliminar', pode_atender: true, o_que_sera_atendido: '',
    tem_capacidade: true, o_que_falta: '', prazo_atendimento_declarado: '',
    premissa_prazo: '', esforco_declarado: '', depende_contratacao: false,
  })
  const [susp, setSusp] = useState({ motivo: '', insumo_de: 'cliente' })
  const [fund, setFund] = useState<Record<string, string>>({})

  async function acao(fn: () => Promise<any>) {
    onErro(null); setOcupado(true)
    try { await fn(); await onMudou() }
    catch (e: any) {
      const d = e?.response?.data?.detail
      onErro(typeof d === 'string' ? d : 'Não foi possível concluir a ação.')
    } finally { setOcupado(false) }
  }

  const agendaAberta = (r.agendas || []).find((a: any) => !a.realizada)

  const concluirPassagem = () => acao(async () => {
    if (!agendaAberta) throw { response: { data: { detail: 'Não há agenda de passagem em aberto.' } } }
    if (pass.entendimento_acordado.trim().length < 10)
      throw { response: { data: { detail: 'Descreva o entendimento acordado (mínimo 10 caracteres).' } } }
    // Secao 7: sem os dois links a passagem nao produz efeito e a agenda tem de ser refeita.
    if (!pass.gravacao_url || !pass.transcricao_url)
      throw { response: { data: { detail: 'Informe a gravação e a transcrição: sem as duas a passagem não produz efeito e a agenda precisa ser refeita.' } } }
    if (!pass.gestor_tecnico_id)
      throw { response: { data: { detail: 'Indique o gestor técnico: é ele quem dá o de acordo pela Tecnologia.' } } }
    const p: any = { ...pass }
    Object.keys(p).forEach((k) => { if (!p[k]) delete p[k] })
    p.entendimento_acordado = pass.entendimento_acordado
    await api.post(`/v1/rdc/${r.id}/agendas/${agendaAberta.id}/concluir`, p)
  })

  const enviarParecer = () => acao(async () => {
    const p: any = { ...par }
    Object.keys(p).forEach((k) => { if (p[k] === '') delete p[k] })
    await api.post(`/v1/rdc/${r.id}/pareceres`, p)
    setPar({ ...par, o_que_sera_atendido: '', o_que_falta: '', esforco_declarado: '' })
  })

  return (
    <div className="space-y-4">
      <div className="flex gap-1 flex-wrap">
        {([['passagem', 'Passagem'], ['parecer', 'Parecer'],
           ['decisao', 'Decisões'], ['prazo', 'Prazo']] as const).map(([k, t]) => (
          <button key={k} onClick={() => setAba(k as any)}
                  className={`px-3 py-1 rounded-md text-xs border transition ${
                    aba === k ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ---------- passagem ---------- */}
      {aba === 'passagem' && (
        <div className="space-y-3">
          {!agendaAberta ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma agenda de passagem em aberto. Marque uma agenda acima para registrar
              o entendimento.
            </p>
          ) : (
            <>
              <Campo rot="Entendimento acordado na passagem">
                <textarea rows={3} className={inputCls} value={pass.entendimento_acordado}
                          placeholder="O que ficou acordado entre Comercial e Tecnologia."
                          onChange={(e) => setPass({ ...pass, entendimento_acordado: e.target.value })} />
              </Campo>
              <Campo rot="Plano de ações">
                <textarea rows={2} className={inputCls} value={pass.plano_acoes}
                          onChange={(e) => setPass({ ...pass, plano_acoes: e.target.value })} />
              </Campo>
              <div className="grid gap-3 sm:grid-cols-2">
                <Campo rot="Reclassificação técnica">
                  <select className={inputCls} value={pass.tipo_tecnico}
                          onChange={(e) => setPass({ ...pass, tipo_tecnico: e.target.value })}>
                    <option value="">Mantém {r.tipo_comercial}</option>
                    {['A', 'B', 'C', 'D', 'E'].map((t) => <option key={t} value={t}>Tipo {t}</option>)}
                  </select>
                </Campo>
                <Campo rot="Gestor técnico · dá o de acordo">
                  <select className={inputCls} value={pass.gestor_tecnico_id}
                          onChange={(e) => setPass({ ...pass, gestor_tecnico_id: e.target.value })}>
                    <option value="">Selecione…</option>
                    {tecnicos.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} · {u.diretoria}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo rot="Frente técnica">
                  <select className={inputCls} value={pass.frente_tecnica}
                          onChange={(e) => setPass({ ...pass, frente_tecnica: e.target.value })}>
                    <option value="">—</option>
                    <option value="fabrica">Fábrica</option>
                    <option value="servicos">Serviços</option>
                  </select>
                </Campo>
                <Campo rot="Link da gravação · obrigatório">
                  <input className={inputCls} placeholder="https://…" value={pass.gravacao_url}
                         onChange={(e) => setPass({ ...pass, gravacao_url: e.target.value })} />
                </Campo>
                <Campo rot="Link da transcrição · obrigatório">
                  <input className={inputCls} placeholder="https://…" value={pass.transcricao_url}
                         onChange={(e) => setPass({ ...pass, transcricao_url: e.target.value })} />
                </Campo>
              </div>
              <p className="text-xs text-muted-foreground">
                Gravação e transcrição são obrigatórias: sem as duas a passagem não produz
                efeito e a agenda precisa ser refeita, sem reabrir prazo. Concluir dispara o
                “de acordo” por e-mail para Comercial e Tecnologia — o RDC só avança quando
                os dois aceitarem.
              </p>
              <button onClick={concluirPassagem}
                      disabled={ocupado || !pass.gravacao_url || !pass.transcricao_url
                                || !pass.gestor_tecnico_id
                                || pass.entendimento_acordado.trim().length < 10}
                      className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground
                                 hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2">
                {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
                Concluir passagem e pedir de acordo
              </button>
            </>
          )}
        </div>
      )}

      {/* ---------- parecer ---------- */}
      {aba === 'parecer' && (
        <div className="space-y-3">
          {(r.pareceres || []).map((p: any) => (
            <div key={p.id} className="rounded-md border px-3 py-2 text-sm">
              <div className="font-medium capitalize">{p.etapa}</div>
              <div className="text-muted-foreground text-xs mt-0.5">
                {p.pode_atender ? 'Pode atender' : 'Não pode atender'}
                {p.prazo_atendimento_declarado && ` · prazo declarado: ${p.prazo_atendimento_declarado}`}
              </div>
              {p.o_que_sera_atendido && <div className="mt-1">{p.o_que_sera_atendido}</div>}
              {p.o_que_falta && <div className="mt-1 text-amber-600">Falta: {p.o_que_falta}</div>}
            </div>
          ))}

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo rot="Etapa">
              <select className={inputCls} value={par.etapa}
                      onChange={(e) => setPar({ ...par, etapa: e.target.value })}>
                <option value="preliminar">Preliminar</option>
                <option value="conclusivo">Conclusivo</option>
              </select>
            </Campo>
            <Campo rot="Prazo de atendimento declarado">
              <input className={inputCls} placeholder="ex.: sprint de outubro" value={par.prazo_atendimento_declarado}
                     onChange={(e) => setPar({ ...par, prazo_atendimento_declarado: e.target.value })} />
            </Campo>
          </div>
          <div className="flex gap-4 flex-wrap text-sm">
            {([['pode_atender', 'Pode atender'], ['tem_capacidade', 'Tem capacidade'],
               ['depende_contratacao', 'Depende de contratação']] as const).map(([k, t]) => (
              <label key={k} className="inline-flex items-center gap-2">
                <input type="checkbox" checked={(par as any)[k]}
                       onChange={(e) => setPar({ ...par, [k]: e.target.checked } as any)} />
                {t}
              </label>
            ))}
          </div>
          <Campo rot="O que será atendido">
            <textarea rows={2} className={inputCls} value={par.o_que_sera_atendido}
                      onChange={(e) => setPar({ ...par, o_que_sera_atendido: e.target.value })} />
          </Campo>
          <Campo rot="O que falta">
            <textarea rows={2} className={inputCls} value={par.o_que_falta}
                      onChange={(e) => setPar({ ...par, o_que_falta: e.target.value })} />
          </Campo>
          <Campo rot="Esforço declarado">
            <input className={inputCls} placeholder="ex.: 120 h" value={par.esforco_declarado}
                   onChange={(e) => setPar({ ...par, esforco_declarado: e.target.value })} />
          </Campo>
          <p className="text-xs text-muted-foreground">
            Parecer conclusivo que depende de contratação abre automaticamente a manifestação
            do Financeiro; acima do teto de 20 dias, a deliberação da Presidência.
          </p>
          <button onClick={enviarParecer} disabled={ocupado}
                  className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground
                             hover:opacity-90 disabled:opacity-60">
            Registrar parecer
          </button>
        </div>
      )}

      {/* ---------- decisoes ---------- */}
      {aba === 'decisao' && (
        <div className="space-y-3">
          {(r.aprovacoes || []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma manifestação pendente. Elas são abertas pelo parecer conclusivo, não à mão.
            </p>
          )}
          {(r.aprovacoes || []).map((a: any) => (
            <div key={a.id} className="rounded-md border px-3 py-3 space-y-2">
              <div className="text-sm font-medium capitalize">
                {a.requerida_de === 'financeiro' ? 'Diretor Financeiro' : 'Diretor Presidente'}
                <span className={`ml-2 text-xs font-normal ${
                  a.status === 'pendente' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  {a.status}
                </span>
              </div>
              {a.fundamentacao && <div className="text-sm text-muted-foreground">{a.fundamentacao}</div>}
              {a.status === 'pendente' && (
                <>
                  <textarea rows={2} className={inputCls} placeholder="Fundamentação da decisão"
                            value={fund[a.id] || ''}
                            onChange={(e) => setFund({ ...fund, [a.id]: e.target.value })} />
                  <div className="flex gap-2 flex-wrap">
                    {(APROVACAO_OPCOES[a.requerida_de] || []).map((o) => (
                      <button key={o.v} disabled={ocupado}
                              onClick={() => acao(() => api.post(
                                `/v1/rdc/${r.id}/aprovacoes/${a.id}/decidir`,
                                { status: o.v, fundamentacao: fund[a.id] || undefined }))}
                              className={`rounded-md border px-3 py-1.5 text-sm transition ${TOM[o.tom]}`}>
                        {o.t}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <Gavel className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    A decisão é registrada uma vez e não se reabre.
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------- prazo ---------- */}
      {aba === 'prazo' && (
        <div className="space-y-3">
          {r.suspenso ? (
            <>
              <p className="text-sm">
                Prazo suspenso. O relógio volta a correr a partir da retomada — o tempo de
                espera não conta contra a Tecnologia.
              </p>
              <button onClick={() => acao(() => api.post(`/v1/rdc/${r.id}/retomar`))} disabled={ocupado}
                      className="rounded-md border px-4 py-2 text-sm hover:bg-muted
                                 inline-flex items-center gap-2">
                <PlayCircle className="h-4 w-4" /> Retomar prazo
              </button>
            </>
          ) : (
            <>
              <Campo rot="Motivo da suspensão">
                <textarea rows={2} className={inputCls} value={susp.motivo}
                          placeholder="Qual insumo está faltando."
                          onChange={(e) => setSusp({ ...susp, motivo: e.target.value })} />
              </Campo>
              <Campo rot="Insumo pendente de">
                <select className={inputCls} value={susp.insumo_de}
                        onChange={(e) => setSusp({ ...susp, insumo_de: e.target.value })}>
                  <option value="cliente">Cliente</option>
                  <option value="comercial">Comercial</option>
                </select>
              </Campo>
              <button
                onClick={() => acao(async () => {
                  if (susp.motivo.trim().length < 5)
                    throw { response: { data: { detail: 'Descreva o motivo da suspensão.' } } }
                  await api.post(`/v1/rdc/${r.id}/suspender`, susp)
                })}
                disabled={ocupado}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted inline-flex items-center gap-2">
                <PauseCircle className="h-4 w-4" /> Suspender prazo
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
